/* eslint-disable no-undef-init */
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Map, {
	Layer,
	MapRef,
	Marker,
	NavigationControl,
	Source,
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Spinner, Typography } from 'design_system/src';
import moment from 'moment';
import type { FeatureCollection } from 'geojson';

import {
	InterestPointType,
	InterestPointTypeValues,
} from 'models/geozon/enums/interestPoint.enum';
import Supercluster from 'supercluster';
import { ReactComponent as Search } from 'design_system/src/static/icons/search.svg';
import { ReactComponent as Checkbox } from 'design_system/src/static/icons/checkmark.svg';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-sm.svg';
import { ReactComponent as GPS } from 'design_system/src/static/icons/gps.svg';
import { ReactComponent as Target } from 'design_system/src/static/icons/target.svg';
import { GeotileHistoryLocationDto } from 'models/geotile/interfaces/geotile.interface';

import { getUser } from 'localStorage/authStorage';
import { isDriverUser } from 'models/user-management/helpers/driver-check';
import { geotileMapContext } from './GeotileMapContext';

import './geotileMap.scss';
import {
	DeviceLocation,
	hotspotHeatLayerDefinition,
	hotspotLayerDefinition,
} from './GeotileMap.constants';
import SauronChat from './SauronChat';

const HOTSPOT_LAYER_ID = 'interest_points_circles';

/** Same purple as geofence Mapbox layers (legend icon + polygons stay aligned). */
const GEOFENCE_PURPLE_RGB = '155, 89, 182';
const GEOFENCE_LEGEND_ICON_COLOR = `rgb(${GEOFENCE_PURPLE_RGB})`;
const GEOFENCE_FILL = `rgba(${GEOFENCE_PURPLE_RGB}, 0.15)`;
const GEOFENCE_FILL_OUTLINE = `rgba(${GEOFENCE_PURPLE_RGB}, 0.8)`;
const GEOFENCE_LINE = `rgba(${GEOFENCE_PURPLE_RGB}, 0.9)`;

const SPREAD_RADIUS_DEGREES = 0.0002;

const spreadCoLocatedPoints = (
	points: DeviceLocation[],
): Array<{
	type: 'Feature';
	geometry: { type: 'Point'; coordinates: [number, number] };
	properties: DeviceLocation;
}> => {
	const locationKey = (lat: number, lng: number) =>
		`${lat.toFixed(6)}_${lng.toFixed(6)}`;
	const groups = new globalThis.Map<string, DeviceLocation[]>();

	points.forEach((point) => {
		const lat = point.location.latitude;
		const lng = point.location.longitude;
		const key = locationKey(lat, lng);
		const list = groups.get(key) ?? [];
		list.push(point);
		groups.set(key, list);
	});

	const features: Array<{
		type: 'Feature';
		geometry: { type: 'Point'; coordinates: [number, number] };
		properties: DeviceLocation;
	}> = [];

	Array.from(groups.values()).forEach((group) => {
		const [first] = group;
		const lat = first.location.latitude;
		const lng = first.location.longitude;
		const latRad = (lat * Math.PI) / 180;

		if (group.length === 1) {
			features.push({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [lng, lat],
				},
				properties: group[0],
			});
			return;
		}

		group.forEach((point: DeviceLocation, i: number) => {
			const angle = (2 * Math.PI * i) / group.length;
			const lngOffset =
				(SPREAD_RADIUS_DEGREES * Math.cos(angle)) / Math.cos(latRad);
			const latOffset = SPREAD_RADIUS_DEGREES * Math.sin(angle);
			features.push({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [lng + lngOffset, lat + latOffset],
				},
				properties: point,
			});
		});
	});

	return features;
};

const formatDateWithCityOffset = (date?: moment.MomentInput) => {
	const user = getUser();
	const company = user?.company;

	let utcOffset = -360;

	if (
		company &&
		company.city &&
		company.city !== null &&
		typeof company.city === 'object' &&
		'utcOffset' in company.city &&
		typeof company.city.utcOffset === 'number'
	) {
		utcOffset = company.city.utcOffset;
	}

	return moment.utc(date).utcOffset(utcOffset);
};

export const GeotileMap = () => {
	const mapRef = useRef<MapRef>(null);
	const isDriver = isDriverUser();

	const {
		loading,
		loadingHistory,
		geofences,
		showGeofences,
		setShowGeofences,
		gpsLocations,
		gpsHistory,
		selectedMarker,
		setSelectedMarker,
	} = useContext(geotileMapContext);

	const [query, setQuery] = useState('');
	const [cursor, setCursor] = useState('grab');
	const [historyFrom, setHistoryFrom] = useState<string>('');
	const [historyTo, setHistoryTo] = useState<string>('');

	const getInitialViewState = () => {
		const user = getUser();
		const company = user?.company;

		if (
			company &&
			company.city &&
			company.city !== null &&
			typeof company.city === 'object' &&
			'location' in company.city &&
			company.city.location &&
			typeof company.city.location === 'object' &&
			company.city.location !== null &&
			'latitude' in company.city.location
		) {
			const cityLocation = company.city.location as {
				latitude: number;
				longitude: number;
				zoom?: number;
			};
			return {
				longitude: cityLocation.longitude,
				latitude: cityLocation.latitude,
				zoom: cityLocation.zoom ?? 10,
			};
		}

		return {
			longitude: -99.12766,
			latitude: 19.42847,
			zoom: 10,
		};
	};

	const [viewState, setViewState] = useState(getInitialViewState());

	const [interestPointTypeFilter, setInterestPointTypeFilter] = useState<{
		[key: string]: boolean;
	}>({
		[InterestPointType.signalVehicle]: true,
		[InterestPointType.noSignalVehicle]: true,
	});

	const [selectedHotspot, setSelectedHotspot] =
		useState<GeotileHistoryLocationDto>();

	useEffect(() => {
		setHistoryFrom('');
		setHistoryTo('');
		setSelectedHotspot(undefined);
	}, [selectedMarker?._id]);

	useEffect(() => {
		if (!selectedMarker) return;
		if (!gpsHistory.length) return;
		if (historyFrom || historyTo) return;

		const timestamps = gpsHistory
			.map((p) => moment(p.timestamp))
			.filter((m) => m.isValid());
		if (!timestamps.length) return;

		const minTs = moment.min(timestamps);
		const maxTs = moment.max(timestamps);
		setHistoryFrom(minTs.format('YYYY-MM-DDTHH:mm'));
		setHistoryTo(maxTs.format('YYYY-MM-DDTHH:mm'));
	}, [gpsHistory, selectedMarker, historyFrom, historyTo]);

	const filteredGpsHistory = useMemo(() => {
		if (!selectedMarker) return [];
		if (!gpsHistory?.length) return [];

		const fromMoment = historyFrom ? moment(historyFrom) : null;
		const toMoment = historyTo ? moment(historyTo) : null;

		return gpsHistory.filter((p) => {
			const ts = moment(p.timestamp);
			if (!ts.isValid()) return false;
			if (fromMoment && fromMoment.isValid() && ts.isBefore(fromMoment))
				return false;
			if (toMoment && toMoment.isValid() && ts.isAfter(toMoment)) return false;
			return true;
		});
	}, [gpsHistory, historyFrom, historyTo, selectedMarker]);

	useEffect(() => {
		setSelectedHotspot(undefined);
	}, [historyFrom, historyTo]);

	const supercluster = useMemo(
		() =>
			new Supercluster({
				radius: 75,
				maxZoom: 20,
			}),
		[],
	);

	const handleMapClick = (event: mapboxgl.MapLayerMouseEvent) => {
		const map = mapRef.current?.getMap();
		if (!map) return;

		if (!map.getLayer(HOTSPOT_LAYER_ID)) return;

		const features = map.queryRenderedFeatures(event.point, {
			layers: [HOTSPOT_LAYER_ID],
		});

		const pointStr = features?.[0]?.properties?.point;
		if (!pointStr) return;

		setSelectedHotspot(JSON.parse(pointStr));
	};

	const hotspotsLayer = useMemo(() => {
		const source: FeatureCollection = {
			type: 'FeatureCollection',
			features: filteredGpsHistory.map((point, i) => ({
				id: `${point.tile_uuid}_${i}`,
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [point.longitude, point.latitude],
				},
				properties: {
					weight: filteredGpsHistory.length ? 6 / filteredGpsHistory.length : 0,
					point,
				},
			})),
		};
		return source?.features?.length ? (
			<Source type="geojson" data={source}>
				<Layer {...hotspotHeatLayerDefinition} />
				<Layer {...hotspotLayerDefinition} />
			</Source>
		) : null;
	}, [filteredGpsHistory]);

	const geofenceLayer = useMemo(() => {
		if (isDriver || !showGeofences) return null;
		if (!geofences?.length) return null;

		const EARTH_RADIUS_M = 6371008.8;
		const toRad = (deg: number) => (deg * Math.PI) / 180;
		const toDeg = (rad: number) => (rad * 180) / Math.PI;

		const destinationPoint = (
			lng: number,
			lat: number,
			bearingDeg: number,
			distanceM: number,
		) => {
			const δ = distanceM / EARTH_RADIUS_M;
			const θ = toRad(bearingDeg);
			const φ1 = toRad(lat);
			const λ1 = toRad(lng);

			const sinφ1 = Math.sin(φ1);
			const cosφ1 = Math.cos(φ1);
			const sinδ = Math.sin(δ);
			const cosδ = Math.cos(δ);

			const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * Math.cos(θ);
			const φ2 = Math.asin(sinφ2);
			const y = Math.sin(θ) * sinδ * cosφ1;
			const x = cosδ - sinφ1 * sinφ2;
			const λ2 = λ1 + Math.atan2(y, x);

			return { lng: ((toDeg(λ2) + 540) % 360) - 180, lat: toDeg(φ2) };
		};

		const circlePolygon = (lng: number, lat: number, radiusM: number) => {
			const steps = 64;
			const coords: [number, number][] = [];
			for (let i = 0; i <= steps; i += 1) {
				const bearing = (i * 360) / steps;
				const p = destinationPoint(lng, lat, bearing, radiusM);
				coords.push([p.lng, p.lat]);
			}
			return coords;
		};

		const source: FeatureCollection = {
			type: 'FeatureCollection',
			features: geofences.map((g) => {
				const [lng, lat] = g.center.coordinates;
				return {
					type: 'Feature',
					geometry: {
						type: 'Polygon',
						coordinates: [circlePolygon(lng, lat, g.radiusMeters)],
					},
					properties: {
						_id: g._id,
						name: g.name,
					},
				};
			}),
		};

		return (
			<Source id="geofences" type="geojson" data={source}>
				<Layer
					id="geofences-fill"
					type="fill"
					paint={{
						'fill-color': GEOFENCE_FILL,
						'fill-outline-color': GEOFENCE_FILL_OUTLINE,
					}}
				/>
				<Layer
					id="geofences-outline"
					type="line"
					paint={{
						'line-color': GEOFENCE_LINE,
						'line-width': 2,
					}}
				/>
			</Source>
		);
	}, [geofences, showGeofences, isDriver]);

	const markers = useMemo(() => {
		if (!gpsLocations) return [];
		const initialPointsFiltered: DeviceLocation[] = Object.values(
			gpsLocations,
		).filter(
			(point) =>
				(point.vehicleData?.internalId?.includes(query) ||
					point.uuid.includes(query) ||
					point.vehicleData?.description?.includes(query)) &&
				interestPointTypeFilter[point.locationType],
		);
		supercluster.load(spreadCoLocatedPoints(initialPointsFiltered));

		const SelectedIcon: any = selectedMarker
			? InterestPointTypeValues[selectedMarker.locationType].icon
			: null;

		const baseMarkers = selectedMarker
			? [
					<Marker
						longitude={selectedMarker.location.longitude}
						latitude={selectedMarker.location.latitude}
						onClick={() => {
							onClickHandler(selectedMarker);
						}}
					>
						<div
							className={`br_xxl main_marker cursor_pointer ${
								InterestPointTypeValues[selectedMarker.locationType].color
							} selected_marker`}
						>
							<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
								<SelectedIcon className="dim_lg icon" />
								<Typography weight="600" scale="xsmall">
									{selectedMarker.vehicleData?.internalId}
								</Typography>
							</div>
							<div className="marker_details p_x_md flex_center_col">
								<Typography weight="600" scale="xxsmall">
									{formatDateWithCityOffset(
										selectedMarker.location.updatedAt,
									).format('DD/MM/YYYY')}
								</Typography>
								<Typography weight="400" scale="xxsmall">
									{formatDateWithCityOffset(
										selectedMarker.location.updatedAt,
									).format('HH:mm')}
								</Typography>
							</div>
							<span className="arrow" />
						</div>
					</Marker>,
			  ]
			: supercluster
					.getClusters([-180, -85, 180, 85], Math.round(viewState.zoom))
					.map((cluster) => {
						const [longitude, latitude] = cluster.geometry.coordinates;

						if (cluster.properties.cluster) {
							return (
								<Marker
									key={`cluster-${cluster.id}`}
									longitude={longitude}
									latitude={latitude}
									onClick={() => handleClusterClick(cluster)}
								>
									<div className="cluster_1">
										<div className="cluster_2">
											<div className="cluster_3 dim_xxxl">
												<Typography
													weight="600"
													scale="xsmall"
													className="cluster_count"
												>
													{cluster.properties.point_count}
												</Typography>
											</div>
										</div>
									</div>
								</Marker>
							);
						}
						const point = cluster.properties as DeviceLocation;
						const Icon = InterestPointTypeValues[point.locationType].icon;
						return (
							<Marker
								key={point._id}
								longitude={longitude}
								latitude={latitude}
								onClick={() => {
									onClickHandler(point);
								}}
							>
								<div
									className={`br_xxl main_marker cursor_pointer ${
										InterestPointTypeValues[point.locationType].color
									}`}
								>
									<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
										<Icon className="dim_lg icon" />
										<Typography weight="600" scale="xsmall">
											{point.vehicleData?.internalId}
										</Typography>
									</div>
									<div className="marker_details p_x_md flex_center_col">
										<Typography weight="600" scale="xxsmall">
											{formatDateWithCityOffset(
												point.location.updatedAt,
											).format('DD/MM/YYYY')}
										</Typography>
										<Typography weight="400" scale="xxsmall">
											{formatDateWithCityOffset(
												point.location.updatedAt,
											).format('HH:mm')}
										</Typography>
									</div>
									<span className="arrow" />
								</div>
							</Marker>
						);
					});
		const selectedHotspotMarker =
			selectedHotspot && selectedMarker
				? [
						<Marker
							key="hotspot_data"
							longitude={selectedHotspot.longitude}
							latitude={selectedHotspot.latitude}
							onClick={() => {
								setSelectedHotspot(undefined);
							}}
						>
							<div className="br_xxl main_marker cursor_pointer primary selected_marker">
								<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
									<GPS className="dim_lg icon" />
									<Typography weight="600" scale="xsmall">
										{selectedMarker.vehicleData?.internalId}
									</Typography>
								</div>
								<div className="marker_details p_x_md flex_center_col">
									<Typography weight="600" scale="xxsmall">
										{formatDateWithCityOffset(selectedHotspot.timestamp).format(
											'DD/MM/YYYY',
										)}
									</Typography>
									<Typography weight="400" scale="xxsmall">
										{formatDateWithCityOffset(selectedHotspot.timestamp).format(
											'HH:mm',
										)}
									</Typography>
								</div>
								<span className="arrow" />
							</div>
						</Marker>,
				  ]
				: [];

		return [...baseMarkers, selectedHotspotMarker];
	}, [
		supercluster,
		viewState.zoom,
		selectedMarker,
		selectedHotspot,
		gpsLocations,
		query,
		interestPointTypeFilter,
	]);

	const handleClusterClick = useCallback(
		(cluster) => {
			const clusterExpansionZoom = supercluster.getClusterExpansionZoom(
				cluster.id,
			);
			mapRef.current?.flyTo({
				center: cluster.geometry.coordinates,
				zoom: clusterExpansionZoom,
			});
		},
		[supercluster, viewState],
	);

	const onClickHandler = (point: DeviceLocation) => {
		if (selectedMarker?._id === point._id) {
			setSelectedMarker(undefined);
			return;
		}
		setSelectedMarker(point);
	};

	useEffect(() => {
		setSelectedHotspot(undefined);
		if (selectedMarker) {
			mapRef.current?.flyTo({
				center: [
					selectedMarker.location.longitude,
					selectedMarker.location.latitude,
				],
				zoom: 15,
			});
		}
	}, [selectedMarker]);

	// Lock viewport so pinch-zoom controls the map, not the browser
	useEffect(() => {
		const meta = document.querySelector('meta[name="viewport"]');
		if (!meta) return () => {};
		const original = meta.getAttribute('content') ?? '';
		meta.setAttribute(
			'content',
			'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no',
		);
		return () => {
			meta.setAttribute('content', original);
		};
	}, []);

	return (
		<div className="dim_100_per flex_content_col inner_container bg_neutral_300 geotile-map-root">
			<div className="geotile_topbar">
				<div className="geotile_page_title">Mapa de activos</div>
			</div>
			<div className="flex_grow_1 overflow_hidden pos_relative">
				<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
					{loading && (
						<div className="pos_absolute center_x br_md bg_neutral_0 p_md z_index_30 m_t_xl flex_center flex_gap_md">
							<Spinner className="dim_xxl" />
							<div>
								<Typography scale="medium" weight="600" className="m_b_xxs">
									Cargando mapa...
								</Typography>
								<Typography scale="small" weight="400" className="">
									📍 No tardaremos mucho.
								</Typography>
							</div>
						</div>
					)}
					{loadingHistory && (
						<div className="pos_absolute center_x br_md bg_neutral_0 p_md z_index_30 m_t_xl flex_center flex_gap_md">
							<Spinner className="dim_xxl" />
							<div>
								<Typography scale="medium" weight="600" className="m_b_xxs">
									Cargando puntos de interes...
								</Typography>
								<Typography scale="small" weight="400" className="">
									📍 No tardaremos mucho.
								</Typography>
							</div>
						</div>
					)}
					<div className="repossession_map_input_cont ">
						<input
							type="text"
							className="repossession_map_input shadow_hard"
							placeholder="Buscar activo"
							value={query}
							onChange={(e) => setQuery(e.target.value.toUpperCase())}
						/>
						<Search className="repossession_map_search_icon dim_lg text_primary_300" />
					</div>
					<div className="devices_map_legend">
						<div className=" bg_neutral_0 br_md p_md m_b_md">
							{!isDriver && (
								<div className="display_flex flex_align_center flex_gap_xs m_b_sm">
									<Target
										className="dim_xl"
										style={{ color: GEOFENCE_LEGEND_ICON_COLOR }}
									/>
									<Typography
										scale="small"
										weight="600"
										className="flex_grow_1"
									>
										Geocercas
									</Typography>
									<div
										className={`repossession_map_input_checkbox br_xs m_l_md ${
											showGeofences ? 'selected' : ''
										}`}
										onClick={() => setShowGeofences(!showGeofences)}
										onKeyDown={() => setShowGeofences(!showGeofences)}
										role="checkbox"
										aria-checked={showGeofences}
										tabIndex={0}
									>
										<Checkbox className="repossession_map_input_checkbox_icon center" />
									</div>
								</div>
							)}
							{Object.entries(InterestPointTypeValues)
								.filter(([key]) =>
									[
										InterestPointType.signalVehicle,
										InterestPointType.noSignalVehicle,
									].includes(key as InterestPointType),
								)
								.map(([key, type]) => (
									<div
										key={key}
										className="display_flex flex_align_center flex_gap_xs"
									>
										<type.icon className={` text_${type.realColor} dim_xl`} />
										<Typography
											scale="small"
											weight="600"
											className="flex_grow_1"
										>
											{type.title}
										</Typography>
										<div
											className={`repossession_map_input_checkbox br_xs m_l_md ${
												interestPointTypeFilter[key] ? 'selected' : ''
											}`}
											onClick={() =>
												setInterestPointTypeFilter({
													...interestPointTypeFilter,
													[key]: !interestPointTypeFilter[key],
												})
											}
											onKeyDown={() =>
												setInterestPointTypeFilter({
													...interestPointTypeFilter,
													[key]: !interestPointTypeFilter[key],
												})
											}
											role="checkbox"
											aria-checked={interestPointTypeFilter[key]}
											tabIndex={0}
										>
											<Checkbox className="repossession_map_input_checkbox_icon center" />
										</div>
									</div>
								))}
						</div>
					</div>
					{selectedMarker && (
						<>
							<div className="devices_map_status m_b_md">
								<div className=" bg_neutral_0 br_md m_b_md shadow_hard overflow_hidden">
									<div className="p_y_md p_x_sm border_b_1 border_primary_100 border_b_solid flex_center flex_justify_between">
										<Typography scale="large" weight="600">
											{selectedMarker.vehicleData?.internalId}
										</Typography>
										<Close
											className="dim_xl border_transparent cursor_pointer"
											onClick={() => setSelectedMarker(undefined)}
										/>
									</div>
									<div className="p_y_md p_x_sm border_b_1 border_primary_100 border_b_solid flex_center flex_gap_lg flex_justify_between">
										<div>
											<Typography
												scale="medium"
												weight="400"
												className="m_b_md"
											>
												{selectedMarker.vehicleData?.description}
											</Typography>
											<Typography
												scale="medium"
												weight="600"
												className="m_b_md"
											>
												GPS:
												<Typography
													scale="medium"
													weight="400"
													component="span"
												>
													{` ${selectedMarker.uuid ?? 'Sin datos'}`}
												</Typography>
											</Typography>
											<Typography scale="xsmall" weight="600">
												Última actualización:
											</Typography>
											<Typography scale="xsmall" weight="400">
												{selectedMarker.location.updatedAt
													? formatDateWithCityOffset(
															selectedMarker.location.updatedAt,
													  ).format('DD/MM/YYYY HH:mm')
													: 'Sin datos'}
											</Typography>
										</div>
									</div>
									{gpsHistory.length > 0 && (
										<div className="p_y_md p_x_sm">
											<div className="flex_center flex_justify_between flex_gap_md m_b_sm">
												<Typography scale="small" weight="600">
													Filtrar historico
												</Typography>
												<Typography
													scale="xsmall"
													weight="400"
													className="text_neutral_600"
												>
													{filteredGpsHistory.length}/{gpsHistory.length}
												</Typography>
											</div>
											<div className="devices_map_history_filter_inputs">
												<label
													className="devices_map_history_filter_field from"
													htmlFor="geotile-history-from"
												>
													<Typography
														scale="xxsmall"
														weight="600"
														className="m_b_xxs"
													>
														Desde
													</Typography>
													<input
														id="geotile-history-from"
														type="datetime-local"
														value={historyFrom}
														onChange={(e) => setHistoryFrom(e.target.value)}
													/>
												</label>
												<label
													className="devices_map_history_filter_field to"
													htmlFor="geotile-history-to"
												>
													<Typography
														scale="xxsmall"
														weight="600"
														className="m_b_xxs"
													>
														Hasta
													</Typography>
													<input
														id="geotile-history-to"
														type="datetime-local"
														value={historyTo}
														onChange={(e) => setHistoryTo(e.target.value)}
													/>
												</label>
											</div>
										</div>
									)}
								</div>
							</div>
							<div className="devices_map_chat">
								<SauronChat device={selectedMarker} autoOpen />
							</div>
						</>
					)}
					<Map
						mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY || ''}
						onMove={(evt) => setViewState(evt.viewState)}
						mapStyle="mapbox://styles/mapbox/streets-v9"
						reuseMaps
						ref={mapRef}
						interactiveLayerIds={
							filteredGpsHistory.length > 0 ? ['interest_points_circles'] : []
						}
						onMouseEnter={() => {
							setCursor('pointer');
						}}
						onMouseLeave={() => setCursor('grab')}
						onClick={handleMapClick}
						cursor={cursor}
						{...viewState}
					>
						<NavigationControl position="bottom-left" showCompass showZoom />
						{geofenceLayer}
						{hotspotsLayer}
						{markers}
					</Map>
				</div>
			</div>
		</div>
	);
};
